import React from "react";
import { motion } from "framer-motion";
import { Disc3, TrendingUp, Users } from "lucide-react";

const PILLARS = [
    {
        icon: Disc3,
        title: "Music distribution",
        desc: "Ship your tracks to every major streaming platform with one upload. Keep 100% of your masters and rights, forever.",
        image: "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/0kw8kx8s_Distribution.png",
    },
    {
        icon: TrendingUp,
        title: "Artist growth",
        desc: "Data-driven tools, smart playlist pitching and audience intelligence built to grow real fans, not vanity metrics.",
        image: "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/xmepbdwc_Grow.png",
    },
    {
        icon: Users,
        title: "Content & collaboration",
        desc: "Studio-grade tools to create, remix and collab with producers, songwriters and visual artists across the globe.",
        image: "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/1qkkm3v9_Discover.png",
    },
];

export const WhatIsMaajja = () => {
    return (
        <section
            id="what"
            className="relative py-28 lg:py-36 overflow-hidden bg-[#030023]"
            data-testid="what-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 relative">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="max-w-3xl mb-16 lg:mb-20"
                >
                    <p className="text-sm uppercase tracking-[0.3em] text-[#F54815] mb-4 font-medium">
                        What is Maajja
                    </p>
                    <h2
                        className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]"
                        data-testid="what-heading"
                    >
                        A complete operating system
                        <br />
                        for the{" "}
                        <span className="text-[#791EE2]">independent</span>{" "}
                        artist.
                    </h2>
                </motion.div>
            </div>

            {/* Full-bleed panel row */}
            <div
                className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 group/row"
                data-testid="pillars-grid"
            >
                {PILLARS.map((p, i) => (
                    <motion.div
                        key={p.title}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.7, delay: i * 0.1 }}
                        className={`group relative aspect-[3/4] md:aspect-[4/5] lg:aspect-[3/4] overflow-hidden ${
                            i > 0 ? "md:border-l" : ""
                        } ${
                            i === 2 ? "md:col-span-2 lg:col-span-1 md:border-l-0 lg:border-l" : ""
                        } border-white/8`}
                        data-testid={`pillar-${i}`}
                    >
                        {/* Image */}
                        <img
                            src={p.image}
                            alt={p.title}
                            className="absolute inset-0 w-full h-full object-cover grayscale brightness-[0.55] contrast-[1.05] transition-all duration-[600ms] ease-out group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-[1.035] lg:group-hover/row:grayscale lg:group-hover/row:brightness-[0.35] lg:group-hover:!brightness-100 lg:group-hover:!grayscale-0"
                        />

                        {/* Dark gradient overlay */}
                        <div
                            className="absolute inset-0 pointer-events-none transition-opacity duration-[600ms] ease-out group-hover:opacity-70"
                            style={{
                                background:
                                    "linear-gradient(to top, rgba(3,0,35,0.98) 0%, rgba(3,0,35,0.88) 25%, rgba(3,0,35,0.45) 55%, rgba(3,0,35,0.15) 80%, rgba(3,0,35,0.05) 100%)",
                            }}
                        />

                        {/* Subtle brand accent on hover */}
                        <div
                            className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-[600ms]"
                            style={{
                                background:
                                    "radial-gradient(circle at 50% 100%, rgba(121,30,226,0.18) 0%, transparent 60%)",
                            }}
                        />

                        {/* Content — anchored at bottom, does not move */}
                        <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10 z-10">
                            <div className="w-12 h-12 rounded-xl bg-white/8 backdrop-blur-md border border-white/10 text-white flex items-center justify-center mb-5 transition-colors duration-500 group-hover:bg-[#791EE2] group-hover:border-[#791EE2]">
                                <p.icon size={22} strokeWidth={1.7} />
                            </div>
                            <h3 className="font-heading text-2xl lg:text-[28px] font-extrabold text-white tracking-tight mb-3 leading-[1.1]">
                                {p.title}
                            </h3>
                            <p className="text-white/70 leading-relaxed font-body text-sm lg:text-[15px] max-w-sm">
                                {p.desc}
                            </p>
                        </div>

                        {/* Panel index (subtle top-left mark) */}
                        <span className="absolute top-6 left-8 lg:left-10 text-[11px] uppercase tracking-[0.25em] text-white/45 font-medium z-10">
                            0{i + 1}
                        </span>
                    </motion.div>
                ))}
            </div>
        </section>
    );
};
